/**
 * MovementsTab — Historial de movimientos de stock con detalle expandible y anulación.
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState } from 'react'
import { Search, ArrowDownCircle, ArrowUpCircle, RefreshCw, Plus, ChevronDown, ChevronRight, XCircle } from 'lucide-react'
import { useMovements, useWarehouses, useCreateMovement, useCancelMovement, type StockMovement } from '../hooks/useInventory'
import { usePermission } from '../../../hooks/usePermission'
import { useTenantLocale, formatNumber, formatDate, formatDateTime } from '../../../lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import { ConfirmModal, useConfirmModal } from '../../../components/ConfirmModal'
import { extractErrorMessage, extractFieldErrors } from '../../../lib/errorUtils'
import MovementModal from './MovementModal'
import { Pagination } from '@/components/ds/Pagination'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'

const TYPE_ICONS: Record<string, { icon: typeof ArrowDownCircle; color: string; label: string }> = {
  inbound:        { icon: ArrowDownCircle, color: 'text-success-solid', label: 'Entrada' },
  outbound:       { icon: ArrowUpCircle, color: 'text-danger-solid', label: 'Salida' },
  transfer_out:   { icon: ArrowUpCircle, color: 'text-warning-solid', label: 'Transfer. salida' },
  transfer_in:    { icon: ArrowDownCircle, color: 'text-blue-500', label: 'Transfer. entrada' },
  adjustment_pos: { icon: RefreshCw, color: 'text-emerald-400', label: 'Ajuste (+)' },
  adjustment_neg: { icon: RefreshCw, color: 'text-danger-solid', label: 'Ajuste (-)' },
}

export default function MovementsTab() {
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR: esta lista la pagina el backend (`?ordering=`).
  const [orden, setOrden] = useState<SortState | null>(null)
  const [search, setSearch] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [movementType, setMovementType] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [expandedId, setExpandedId] = useState('')
  const [cancellingId, setCancellingId] = useState('')
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()

  const { data: whData } = useWarehouses({ active: 'true', page_size: 9999 })
  const { data, isLoading } = useMovements({
    ordering: sortStateToParam(orden),
    search: search || undefined,
    warehouse_id: warehouseId || undefined,
    movement_type: movementType || undefined,
    page,
    page_size: 20,
  })
  const { mutate: createMovement, isPending } = useCreateMovement()
  const { mutate: cancelMovement } = useCancelMovement(cancellingId)

  const movements = data?.movements || []
  const total = data?.count || 0

  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    return `$${formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)}`
  }
  const fmtQty = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    return formatNumber(n, locale, n % 1 !== 0 ? 2 : 0)
  }

  const handleCreate = (formData: any) => {
    createMovement(formData, {
      onSuccess: () => { toast.success('Movimiento registrado'); setModalOpen(false) },
      // El detalle por campo es lo que dice QUÉ falló: sin él, todo error de
      // validación se leía como el genérico «Los datos enviados no son válidos.».
      onError: (e: any) => {
        const mensaje = extractErrorMessage(e, 'Error al crear movimiento')
        const campos = extractFieldErrors(e)
        toast.error(campos ? `${mensaje} ${Object.values(campos).join(' ')}` : mensaje)
      },
    })
  }

  const handleCancel = (m: StockMovement) => {
    setCancellingId(m.id)
    confirm({
      message: `¿Anular el movimiento de ${fmtQty(m.quantity)} ${m.product_name}? Se creará un contra-movimiento inverso.`,
      variant: 'danger',
      onConfirm: () => {
        cancelMovement(undefined, {
          onSuccess: () => { toast.success('Movimiento anulado'); setCancellingId('') },
          onError: (e: any) => { toast.error(extractErrorMessage(e, 'Error al anular')); setCancellingId('') },
        })
      },
    })
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar producto" type="text" placeholder="Buscar producto..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <select aria-label="Todas las bodegas" value={warehouseId} onChange={e => { setWarehouseId(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
          <option value="">Todas las bodegas</option>
          {(whData?.warehouses || []).map(w => (
            <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
          ))}
        </select>
        <select aria-label="Todos los tipos" value={movementType} onChange={e => { setMovementType(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content">
          <option value="">Todos los tipos</option>
          <option value="inbound">Entrada</option>
          <option value="outbound">Salida</option>
          <option value="transfer_out">Transfer. salida</option>
          <option value="transfer_in">Transfer. entrada</option>
          <option value="adjustment_pos">Ajuste (+)</option>
          <option value="adjustment_neg">Ajuste (-)</option>
        </select>
        <div className="ml-auto">
          {can('inventory.movements', 'create') && (
            <button onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
              <Plus size={14} /> Nuevo movimiento
            </button>
          )}
        </div>
      </div>

      <DataTable<StockMovement>
        {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
        columns={[
          { key: 'expandir', header: '', width: 32,
            render: m => (
              <span className="text-content-subtle">
                {expandedId === m.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </span>
            ) },
          { key: 'movement_type', header: 'Tipo', sortable: true,
            sortValue: m => (TYPE_ICONS[m.movement_type] || TYPE_ICONS.inbound).label,
            render: m => {
              const info = TYPE_ICONS[m.movement_type] || TYPE_ICONS.inbound
              const Icon = info.icon
              return (
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${info.color}`}>
                  <Icon size={14} /> {info.label}
                </span>
              )
            } },
          { key: 'product_name', header: 'Producto', sortable: true,
            render: m => (
              <>
                <span className="font-medium text-content">{m.product_name}</span>
                <span className="ml-2 text-xs text-content-subtle font-mono">{m.product_sku}</span>
              </>
            ) },
          { key: 'warehouse_code', header: 'Bodega', sortable: true,
            render: m => <span className="text-content-muted">{m.warehouse_code}</span> },
          { key: 'quantity', header: 'Cantidad', numeric: true,
            sortable: true, sortValue: m => Number(m.quantity),
            render: m => <span className="font-medium">{fmtQty(m.quantity)}</span> },
          { key: 'unit_cost', header: 'Costo Unit.', numeric: true,
            sortable: true, sortValue: m => Number(m.unit_cost),
            render: m => <span className="text-content-muted">{fmt(m.unit_cost)}</span> },
          { key: 'total_cost', header: 'Total', numeric: true,
            sortable: true, sortValue: m => Number(m.total_cost),
            render: m => <span className="font-medium">{fmt(m.total_cost)}</span> },
          { key: 'status', header: 'Estado',
            render: m => (
              <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                m.status === 'cancelled'
                  ? 'bg-danger-surface-strong text-danger-content-soft'
                  : 'bg-success-surface-strong text-success-content'
              }`}>
                {m.status === 'cancelled' ? 'Anulado' : 'Confirmado'}
              </span>
            ) },
          { key: 'created_at', header: 'Fecha',
            sortable: true, sortValue: m => m.created_at,
            render: m => (
              <span className="text-xs text-content-muted">{formatDate(m.created_at, locale)}</span>
            ) },
          { key: 'acciones', header: 'Acc.', width: 64,
            // `stopPropagation` para que anular no expanda la fila de paso: el
            // clic de la fila es lo que abre el detalle.
            render: m => (
              <div className="text-center" onClick={e => e.stopPropagation()}>
                {m.status !== 'cancelled'
                  && !['transfer_out', 'transfer_in'].includes(m.movement_type)
                  && can('inventory.movements', 'approve') && (
                  <CancelButton movementId={m.id} toast={toast} />
                )}
              </div>
            ) },
        ]}
        rows={movements}
        rowKey={m => m.id}
        loading={isLoading}
        empty={
          <EmptyState
            icon={RefreshCw}
            title="Sin movimientos registrados"
            hint="Los ingresos, salidas y ajustes de stock aparecen acá."
          />
        }
        onRowClick={m => setExpandedId(expandedId === m.id ? '' : m.id)}
        rowClassName={m => (m.status === 'cancelled' ? 'opacity-40 line-through' : undefined)}
        rowExpanded={m => expandedId === m.id}
        expandedRow={m => {
          const info = TYPE_ICONS[m.movement_type] || TYPE_ICONS.inbound
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-4 bg-surface-muted text-sm">
              <DetailField label="Tipo" value={m.movement_type_display || info.label} />
              <DetailField label="Producto" value={`${m.product_name} (${m.product_sku})`} />
              <DetailField label="Bodega" value={`${m.warehouse_code} — ${m.warehouse_name}`} />
              <DetailField label="Cantidad" value={fmtQty(m.quantity)} />
              <DetailField label="Costo Unitario" value={fmt(m.unit_cost)} />
              <DetailField label="Costo Total" value={fmt(m.total_cost)} />
              <DetailField label="Referencia" value={m.reference_type ? `${m.reference_type} ${m.reference_id ? `(${String(m.reference_id).slice(0, 8)})` : ''}` : '—'} />
              <DetailField label="Estado" value={m.status === 'cancelled' ? 'Anulado' : 'Confirmado'} />
              <DetailField label="Fecha" value={formatDateTime(m.created_at, locale)} />
              {m.notes && <DetailField label="Notas" value={m.notes} span2 />}
            </div>
          )
        }}
        caption="Movimientos de stock"
      />

      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      <MovementModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreate} isPending={isPending} />
      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function DetailField({ label, value, span2 }: { label: string; value: string; span2?: boolean }) {
  return (
    <div className={span2 ? 'col-span-2 sm:col-span-4' : ''}>
      <p className="text-xs text-content-subtle font-medium mb-0.5">{label}</p>
      <p className="text-sm text-content">{value}</p>
    </div>
  )
}

function CancelButton({ movementId, toast }: { movementId: string; toast: any }) {
  const { mutate, isPending } = useCancelMovement(movementId)
  const { confirm, modalProps } = useConfirmModal()

  const handleClick = () => {
    confirm({
      message: '¿Anular este movimiento? Se creará un contra-movimiento inverso para revertir el stock.',
      variant: 'danger',
      onConfirm: () => {
        mutate(undefined, {
          onSuccess: (data: any) => toast.success(data.message || 'Movimiento anulado'),
          onError: (e: any) => toast.error(extractErrorMessage(e, 'Error al anular')),
        })
      },
    })
  }

  return (
    <>
      <button onClick={handleClick} disabled={isPending} title="Anular movimiento"
        className="p-1.5 rounded-lg hover:bg-danger-surface text-content-subtle hover:text-danger-solid disabled:opacity-40">
        <XCircle size={14} />
      </button>
      <ConfirmModal {...modalProps} />
    </>
  )
}
